import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Sparkles, Search, RotateCw, AlertCircle, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MD } from './Markdown';
import { Logo } from './Logo';
import { streamDifyChat, isDifyConfigured } from '../../lib/difyClient';
import { addXP, XP_PER_QUIZ_CORRECT } from '../../lib/storage';
import { canConsume, consume, getCurrentTier } from '../../lib/quota';
import { toast } from 'sonner';

const TOTAL_QUESTIONS = 15;
const CHIPS = ['Ras', 'Alankar', 'Chhand', 'Bhaktikaal', 'Chhayavad', 'Adhunik Kaal', 'Riti Kaal', 'Bhartiya Kavyashastra', 'Godan', 'Kamayani', 'Premchand', 'Tulsidas', 'Kabir', 'Suryakant Tripathi Nirala', 'Acharya Ramchandra Shukla', 'Hazariprasad Dwivedi', 'Vyakaran', 'Sampraday (Ras/Vakrokti/Dhwani)'];

// Detect option letters in AI response. Matches "(A)" or "A)" or "A:" at line start.
const OPTION_REGEX = /(?:^|\n)\s*[\(\[]?([A-Dक-घ])[\)\]\.\:।]/g;

function detectOptions(text) {
  if (!text) return [];
  const set = new Set();
  let m;
  while ((m = OPTION_REGEX.exec(text)) !== null) {
    let letter = m[1];
    if (letter === 'क') letter = 'A';
    else if (letter === 'ख') letter = 'B';
    else if (letter === 'ग') letter = 'C';
    else if (letter === 'घ') letter = 'D';
    set.add(letter);
  }
  return ['A', 'B', 'C', 'D'].filter(l => set.has(l));
}

// Detect "क्विज़ समाप्त" or "Quiz complete" markers
const DONE_REGEX = /(quiz\s*(complete|समाप्त|खत्म|समाप्ति|finished|over)|क्विज़\s*(समाप्त|खत्म|पूरी|समाप्ति)|final\s*score|final\s*result|कुल\s*स्कोर)/i;

// Detect score from text "Score: 12/15" or "स्कोर: 12 / 15"
const SCORE_REGEX = /(?:score|स्कोर|अंक)[^0-9]*([0-9]{1,2})\s*\/\s*15/i;

// Detect "सही"/"correct" or "गलत"/"wrong" feedback
const CORRECT_REGEX = /(?:सही\s*उत्तर|correct\s*answer|✅|शाबाश|बिल्कुल\s*सही)/i;
const WRONG_REGEX = /(?:गलत\s*उत्तर|wrong\s*answer|❌|incorrect)/i;

export const QuizPage = ({ onBack }) => {
  const [topic, setTopic] = useState('');
  const [pendingTopic, setPendingTopic] = useState('');
  const [started, setStarted] = useState(false);
  const [turns, setTurns] = useState([]); // array of { role: 'user'|'ai', content, options?: ['A','B','C','D'] }
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [score, setScore] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const apiKey = process.env.REACT_APP_DIFY_QUIZ_API_KEY;
  const configured = isDifyConfigured(apiKey);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, streaming]);

  const start = async (chosenTopic) => {
    if (!canConsume('quizzes')) {
      const tier = getCurrentTier();
      toast.error('Daily quiz limit reached', {
        description: tier.id === 'monthly' ? 'Unexpected — retry.' : `${tier.label} allows ${tier.quizzes}/day. Upgrade Prime.`,
      });
      return;
    }
    setStarted(true);
    setTopic(chosenTopic);
    setTurns([]);
    setScore(0);
    setQuestionCount(0);
    setFinished(false);
    setConversationId('');
    consume('quizzes');

    const startQuery = chosenTopic
      ? `कृपया "${chosenTopic}" विषय पर UGC NET / TGT / PGT स्तर की ${TOTAL_QUESTIONS} प्रश्नों की मिनी क्विज़ शुरू करें। हर प्रश्न के साथ 4 विकल्प (A, B, C, D) दीजिए। मैं केवल A, B, C, या D टाइप करके उत्तर दूंगा। कठिनाई स्तर मिश्रित रखें। पहला प्रश्न दें।`
      : `कृपया हिंदी साहित्य / भारतीय काव्यशास्त्र पर UGC NET स्तर की ${TOTAL_QUESTIONS} प्रश्नों की मिनी क्विज़ शुरू करें। हर प्रश्न के साथ 4 विकल्प दीजिए। पहला प्रश्न दें।`;

    await sendToDify(startQuery, true);
  };

  const sendToDify = async (query, isStartTurn = false) => {
    if (!configured) {
      // Demo mode - fake a question
      const demoQ = `**प्रश्न ${questionCount + 1}/15:** यह डेमो मोड है। Dify API key set करें।\n\nA) विकल्प एक\nB) विकल्प दो\nC) विकल्प तीन\nD) विकल्प चार`;
      setTurns(t => [...t, { role: 'user', content: query, hidden: isStartTurn }, { role: 'ai', content: demoQ, options: ['A','B','C','D'] }]);
      if (!isStartTurn) setQuestionCount(c => c + 1);
      return;
    }

    setStreaming(true);
    const ac = new AbortController();
    abortRef.current = ac;
    let answer = '';
    let convId = conversationId;

    // Add user turn (hidden for start), placeholder AI turn
    const aiId = `ai_${Date.now()}`;
    setTurns(prev => [
      ...prev,
      ...(isStartTurn ? [] : [{ role: 'user', content: query }]),
      { id: aiId, role: 'ai', content: '', options: [], streaming: true },
    ]);

    try {
      await streamDifyChat({
        apiKey,
        query,
        conversationId: convId,
        user: 'brahmastra-quiz',
        signal: ac.signal,
        onEvent: (ev) => {
          if (ev.conversation_id) convId = ev.conversation_id;
          if (ev.event === 'message' || ev.event === 'agent_message') {
            answer += ev.answer || '';
            setTurns(prev => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.id === aiId) {
                copy[copy.length - 1] = { ...last, content: answer, options: detectOptions(answer) };
              }
              return copy;
            });
          } else if (ev.event === 'error') {
            throw new Error(ev.message || 'Dify error');
          }
        },
      });

      setConversationId(convId);
      // Final state
      setTurns(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.id === aiId) {
          copy[copy.length - 1] = { ...last, content: answer, options: detectOptions(answer), streaming: false };
        }
        return copy;
      });

      // Track question count + score from response
      if (!isStartTurn) {
        if (CORRECT_REGEX.test(answer)) {
          setScore(s => s + 1);
          addXP(XP_PER_QUIZ_CORRECT);
          window.dispatchEvent(new Event('brahmastra:xp'));
        }
      }
      // increment question count when AI asks a new question (heuristic: has options)
      if (detectOptions(answer).length === 4) {
        setQuestionCount(c => c + 1);
      }
      // Check finish
      const scoreMatch = answer.match(SCORE_REGEX);
      if (scoreMatch) {
        const finalScore = parseInt(scoreMatch[1], 10);
        if (!isNaN(finalScore)) setScore(finalScore);
      }
      if (DONE_REGEX.test(answer) || scoreMatch) {
        setFinished(true);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      toast.error('Quiz failed', { description: String(e.message || e) });
      setTurns(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.id === aiId) {
          copy[copy.length - 1] = { ...last, content: `⚠️ ${e.message}`, streaming: false, error: true };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const pickOption = (letter) => {
    if (streaming || finished) return;
    sendToDify(letter);
  };

  const resetAll = () => {
    abortRef.current?.abort?.();
    setStarted(false);
    setPendingTopic('');
    setTurns([]);
    setConversationId('');
    setScore(0);
    setQuestionCount(0);
    setFinished(false);
  };

  // ----------- Render -----------
  if (!started) {
    return (
      <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-app overflow-y-auto thin-scroll" data-testid="quiz-page" style={{ minWidth: 0 }}>
        <TopBar onBack={onBack} title="Mini Quiz" subtitle={`${TOTAL_QUESTIONS} questions`} />
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 flex-1">
          <div className="anim-fade-up">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Choose a topic for your quiz</h1>
              <p className="mt-1.5 text-[13px] text-muted-foreground">{TOTAL_QUESTIONS} MCQs · click-based · AI-driven</p>
            </div>
            <div className="mt-6 sm:mt-8 max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={pendingTopic}
                  onChange={(e) => setPendingTopic(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pendingTopic.trim()) start(pendingTopic.trim()); }}
                  placeholder="Type any topic — e.g., 'Godan', 'Bhartiya Kavyashastra'..."
                  className="pl-9 h-11 bg-card border-border focus-visible:ring-mint/30"
                  data-testid="topic-input"
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-[10.5px] text-muted-foreground">
                <span>or pick a quick topic ↓</span>
                <span>Press Enter to start</span>
              </div>
              <div className="mt-3 flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto pb-1 px-1 -mx-1">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => start(c)}
                    data-testid={`chip-${c.replace(/\s+/g, '-')}`}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] border border-border bg-card hover:border-mint/40 hover:bg-secondary/60 transition"
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={() => start(pendingTopic.trim() || '')}
                  className="bg-mint text-[#08120e] hover:brightness-110 px-8 h-11"
                  data-testid="start-btn"
                >
                  Start Quiz
                </Button>
              </div>
              {!configured && (
                <div className="mt-4 text-center text-[10.5px] text-amber-400 flex items-center justify-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Dify quiz key not configured — demo mode
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Active quiz - conversation style
  const lastAI = [...turns].reverse().find(t => t.role === 'ai');
  const activeOptions = lastAI && !streaming && !finished ? (lastAI.options || []) : [];

  return (
    <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-app" data-testid="quiz-page">
      <TopBar
        onBack={onBack}
        title="Mini Quiz"
        subtitle={`Topic: ${topic || 'mixed'}`}
        right={
          <div className="text-[12px] text-muted-foreground">
            <span className="text-mint font-semibold">{score}</span>
            {questionCount > 0 && <> · Q {Math.min(questionCount, TOTAL_QUESTIONS)}/{TOTAL_QUESTIONS}</>}
          </div>
        }
      />

      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll" ref={scrollRef}>
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-5 space-y-4">
          {turns.map((t, i) => {
            if (t.role === 'user' && t.hidden) return null;
            return t.role === 'user' ? (
              <div key={i} className="flex justify-end" data-testid="quiz-user-turn">
                <div className="px-3 py-1.5 rounded-full bg-bblue/15 border border-bblue/30 text-bblue text-[12.5px] font-bold">
                  Your answer: {t.content}
                </div>
              </div>
            ) : (
              <div key={t.id || i} className="flex gap-3 anim-fade-up" data-testid="quiz-ai-turn">
                <div className="shrink-0"><Logo size="sm" /></div>
                <div className="flex-1 min-w-0 rounded-2xl px-4 py-3 bg-card border border-border">
                  <div className="text-[13.5px] devanagari leading-relaxed">
                    <MD accent="mint">{t.content || ''}</MD>
                  </div>
                  {t.streaming && (
                    <div className="mt-2 text-muted-foreground text-xs flex items-center gap-1.5">
                      <span className="dot-pulse"><span /><span /><span /></span> Guru ji likh rahe hain...
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Option buttons OR finish */}
      <div className="shrink-0 border-t border-border bg-card/60 glass">
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-3">
          {finished ? (
            <FinishedBar score={score} total={TOTAL_QUESTIONS} onAgain={resetAll} onBack={onBack} />
          ) : activeOptions.length === 4 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="quiz-options-bar">
              {activeOptions.map((letter) => (
                <button
                  key={letter}
                  onClick={() => pickOption(letter)}
                  disabled={streaming}
                  data-testid={`quiz-option-${letter}`}
                  className="px-4 py-3 rounded-xl border border-border bg-secondary/60 hover:bg-mint/15 hover:border-mint/60 transition text-[15px] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ({letter})
                </button>
              ))}
            </div>
          ) : streaming ? (
            <div className="text-center text-muted-foreground text-[11.5px] py-2">Waiting for next question...</div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={resetAll} data-testid="quiz-restart">
                <RotateCw className="w-3.5 h-3.5 mr-1" /> New Topic
              </Button>
              <Button
                onClick={() => sendToDify('अगला प्रश्न दीजिए')}
                size="sm"
                className="bg-mint text-[#08120e] hover:brightness-110"
                data-testid="quiz-next-prompt"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Next Question
              </Button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

const TopBar = ({ onBack, title, subtitle, right }) => (
  <div className="sticky top-0 z-10 border-b border-border bg-card/60 glass">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <button onClick={onBack} data-testid="quiz-back-btn" className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground transition">
        <ArrowLeft className="w-4 h-4" /> Back to chat
      </button>
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-mint" />
        <span className="text-[13px] font-semibold">{title} <span className="text-muted-foreground font-normal">· {subtitle}</span></span>
      </div>
      <div className="w-full sm:w-auto text-left sm:text-right">{right || <span />}</div>
    </div>
  </div>
);

const FinishedBar = ({ score, total, onAgain, onBack }) => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-1" data-testid="quiz-result">
    <div className="flex items-center gap-3">
      <div className="text-2xl">🏆</div>
      <div>
        <div className="text-[15px] font-bold">{score} / {total}</div>
        <div className="text-[10.5px] text-muted-foreground">+{score * XP_PER_QUIZ_CORRECT} XP earned</div>
      </div>
    </div>
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="quiz-back-result">Back to chat</Button>
      <Button onClick={onAgain} size="sm" className="bg-mint text-[#08120e] hover:brightness-110" data-testid="quiz-again">
        <RotateCw className="w-4 h-4 mr-1.5" /> New Quiz
      </Button>
    </div>
  </div>
);

export default QuizPage;
